import { useState } from 'react';
import { useOnMount, useToggle } from '../../../../../../../hooks';
import { isTrue } from '../../../../../../../utils';
import { OrganizationSsoMethod } from '../../../../../../../../typings/enums';
import type { SelectOption } from '../../../../../../../types';
import { useAuthenticationSettingsContext } from '../../../hooks';

// This hook is used to manage the state and logic related to claiming a domain for Google SSO.
const useClaimDomain = () => {
  const {
    isSaveButtonDisabled: canClaimDomain,
    location,
    domains,
    onDeleteDomain,
    router,
  } = useAuthenticationSettingsContext();
  const [isClaimDomainModalOpen, toggleIsClaimDomainModalOpen] = useToggle(false);
  const [isRemoveDomainModalOpen, toggleIsRemoveDomainModalOpen] = useToggle(false);
  const [domainToRemove, setDomainToRemove] = useState<string | null>(null);

  // A "samlAuthenticated", "ssoDomainClaimed", or "domainVerificationError" query parameter may set by the server
  // redirecting the user to the settings page.
  // A "claimDomain=true" query parameter is set by the frontend when the user is redirected to the SSO settings page to
  // claim a domain.
  const {
    samlAuthenticated: samlAuthenticatedDomain,
    ssoDomainClaimed: claimedDomain,
    domainVerificationError,
    claimDomain,
  } = location?.query || {};

  const isModalTriggeredExternally =
    !!(samlAuthenticatedDomain || claimedDomain || domainVerificationError) || isTrue(claimDomain);

  // On mount only, check if we should open the modal to display a message telling the user to check their email or that
  // they've claimed a domain.
  useOnMount(() => {
    if (isModalTriggeredExternally) {
      toggleIsClaimDomainModalOpen();
    }
  });

  const relevantDomains = domains.filter(({ ssoMethod }) => ssoMethod === OrganizationSsoMethod.Google);

  const onChangeDomains = (newValues: SelectOption[]) => {
    const removedDomain = relevantDomains.find(({ domain }) => !newValues.some(({ value }) => value === domain));
    if (removedDomain) {
      setDomainToRemove(removedDomain.domain);
      toggleIsRemoveDomainModalOpen();
    }
  };

  const onConfirmRemoveDomain = async () => {
    if (domainToRemove) {
      await onDeleteDomain(domainToRemove);
    }
  };

  const clearQueryParams = () => {
    if (isModalTriggeredExternally) {
      // Clear out the query parameters.
      router.push(location.pathname);
    }
  };

  return {
    canClaimDomain,
    isClaimDomainModalOpen,
    isRemoveDomainModalOpen,
    relevantDomains,
    toggleIsClaimDomainModalOpen,
    toggleIsRemoveDomainModalOpen,
    onChangeDomains,
    onConfirmRemoveDomain,
    clearQueryParams,
    domainToRemove,
    setDomainToRemove,
  };
};

export default useClaimDomain;
